import type { FunnelAssignment } from '@/utils/funnel'

/**
 * ЧИСТЫЕ ПРАВИЛА РЕКЛАМНЫХ ПЛОЩАДОК — 2026-09-28, к сплит-тесту воронок. Отдельно от pixels.ts и ga.ts,
 * потому что те читают окружение сборки (`import.meta.env`) и под Node не запускаются, а эти правила
 * решают, сколько лидов и с какой ценностью увидит Мета, — их надо проверять без браузера
 * (utils/funnel.test.ts).
 */

/* Явное «выключено». Всё прочее непустое — включено. */
const OFF = new Set(['0', 'false', 'no', 'off'])

/**
 * VITE_MARKETING_PIXELS. Было `Boolean(значение)`, и `"0"` или `"false"` ВКЛЮЧАЛИ пиксели. Теперь
 * 0/false/no/off в любом регистре и пустая строка выключают, 1/true/yes включают.
 *
 * Неизвестное непустое значение — ВКЛЮЧЕНО, как было до правки, и это сознательно: в прод-бандле флаг
 * свёрнут минификатором в `!0`, и какой строкой он задан в прод-.env, из сборки не прочесть. Строгий
 * список «только 1/true/yes» мог бы молча выключить пиксели на проде в день запуска кампаний.
 */
export const flagOn = (raw: unknown): boolean => {
  const value = String(raw ?? '').trim().toLowerCase()
  return value !== '' && !OFF.has(value)
}

/**
 * VITE_FB_LEAD_VALUES="1:X,3:X,4:Y" — ценность Lead в долларах по воронке. Лид воронки 4 — одна почта
 * при регистрации, лид воронок 1 и 3 — контакт после чтения продукта и роли; когда Дима назначит им
 * разную цену, кампании смогут оптимизироваться на ценность. Пока переменной нет — Lead уходит ровно
 * тем же, что сегодня (решение Димы 2026-09-28: ценность пока одна). Кривая пара выбрасывается целиком,
 * а не читается как 0: ценность 0 у Меты — это «лид ничего не стоит», хуже, чем никакой.
 */
export const parseLeadValues = (raw: unknown): ReadonlyMap<number, number> => {
  const values = new Map<number, number>()
  for (const part of String(raw ?? '').split(',')) {
    const match = part.trim().match(/^(\d{1,2})\s*:\s*(\d+(?:\.\d+)?)$/)
    if (!match) continue
    const funnel = Number(match[1])
    const value = Number(match[2])
    if (funnel >= 1 && Number.isFinite(value) && value > 0) values.set(funnel, value)
  }
  return values
}

/** Параметры FB Lead: метка воронки всегда, value и currency — только если у воронки есть цена. */
export const leadParams = (
  params: Readonly<Record<string, string | number>>,
  funnel: number,
  values: ReadonlyMap<number, number>,
): Record<string, string | number> => {
  const value = values.get(funnel)
  return value == null ? { ...params } : { ...params, value, currency: 'USD' }
}

/**
 * ОДИН LEAD НА БРАУЗЕР И ПАРУ «ВОРОНКА × ТЕСТ». Замерено 2026-09-28: повторная отправка формы
 * контакта или регистрации слала второй Lead в Мету — 2 из 2 в воронках 1 и 4, — и оптимизация
 * училась на человеке дважды. Пара, а не один флаг на браузер: человек, перешедший из органики в
 * тест (или из воронки 1 в воронку 4 кликом по другой кампании), — это лид другой кампании.
 * Amplitude (`onboarding_completed`) по-прежнему считает каждую отправку: там это поведение, а не лид.
 */
export const leadSlot = (a: FunnelAssignment): string =>
  `${a.type}:${a.test ?? (a.override ? 'override' : 'organic')}`

/**
 * Стабильный eventID Lead: один и тот же для браузера и пары, чтобы серверный дубль через Conversions
 * API, когда он появится, склеился с браузерным. Из случайного id устройства — ничего о человеке.
 */
export const leadEventId = (slot: string, device: string): string => `lead.${slot}.${device}`

/** Ещё не отправлен ли Lead этой площадкой в этот слот. `sent` — запись из localStorage как есть. */
export const leadAlreadySent = (sent: unknown, vendor: string, slot: string): boolean =>
  Array.isArray(sent) && sent.includes(`${vendor}|${slot}`)

/** Запись после отправки: старые слоты сохраняются, мусор из хранилища — нет. */
export const withLeadSent = (sent: unknown, vendor: string, slot: string): string[] => {
  const list = Array.isArray(sent) ? sent.filter((entry): entry is string => typeof entry === 'string') : []
  const key = `${vendor}|${slot}`
  return list.includes(key) ? list : [...list, key]
}
